<template>
  <div>
    <history-modal-content></history-modal-content>
    <div class="d-flex justify-content-end">
      <div class="d-flex justify-content-between button-group">
        <img src="@/assets/images/common-icons/icon_clear.png" @click="cleanFilters"
             :title="$t('buttons.common.clear_filters')">
        <img src="@/assets/images/common-icons/icon_search.svg" @click="search"
             :title="$t('buttons.common.search')">
      </div>
    </div>
    <div class="search-form">
      <div class="search-form__filters">
        <b-row>
          <b-col>
            <label>{{$t('labels.common.agent')}}</label>
            <multiselect v-model="searchData.agents" :max-height="200"
                         placeholder="" label="name" track-by="id"
                         :options="agents" :multiple="true" :close-on-select="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.agents.commission_type')}}</label>
            <multiselect v-model="searchData.commission_type" :max-height="200"
                         placeholder="" label="name" track-by="key"
                         :options="commissionTypes" :multiple="true" :close-on-select="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.agents.purchase_invoice_number')}}</label>
            <input class="form-control" v-model="searchData.purchase_invoice_number" @keyup.enter="search"
                   placeholder="">
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('labels.common.supplier')}}</label>
            <multiselect v-model="searchData.suppliers" :max-height="200"
                         placeholder="" label="name" track-by="id"
                         :options="suppliers" :multiple="true"
                         :taggable="true" :close-on-select="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.agents.commission_currency')}}</label>
            <multiselect v-model="searchData.currencies" :max-height="200"
                         placeholder="" label="code" track-by="code"
                         :options="currencies" :multiple="true" :close-on-select="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.agents.purchase_invoice_date')}}</label>
            <range-date-picker v-model="searchData.range_date" range lang="en"
                               placeholder=""
                               range-separator="/" :first-day-of-week="1"></range-date-picker>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="4">
            <label>{{$t('labels.common.buyer')}}</label>
            <multiselect v-model="searchData.buyers" :max-height="200"
                         placeholder="" label="name" track-by="id"
                         :options="customers" :multiple="true" :close-on-select="false"></multiselect>
          </b-col>
        </b-row>
      </div>
    </div>
    <div>
      <b-row>
        <b-col cols="4">
          <input class="form-control"
                 placeholder=""
                 v-model="searchData.quick_search">
        </b-col>
        <b-col cols="4" offset="4" class="vertical-center-container d-flex justify-content-end">
          <label for="pagination-toggle" class="margin-right-5">{{$t('labels.common.pagination')}}</label>
          <input id="pagination-toggle" type="checkbox"
                 title="this checkbox needed for toggle pagination bar" v-model="paginationState"
                 @change="togglePagination">
          <save-columns-btn :grid="commissionFeesGridOptions" :grid-name="gridName"
                            class="margin-left-5"></save-columns-btn>
        </b-col>
      </b-row>
    </div>
    <div>
      <b-row>
        <b-col>
          <ag-grid-vue class="ag-theme-balham grid" :gridOptions="commissionFeesGridOptions"
                       :pinnedBottomRowData="purchaseCommissionFeesPinnedRowData" :postProcessPopup="postProcessPopup"
                       :columnDefs="purchaseCommissionFeesColumns" :rowData="commissionFees">
          </ag-grid-vue>
        </b-col>
      </b-row>
      <b-row class="pagination__wrapper" v-if="paginationState">
        <b-col cols="5">
          <div class="pagination-info">{{countOfViewCommissionFees}} {{$t('common_content.of')}}
            {{purchaseAgentCommissionFeesCount}}
          </div>
        </b-col>
        <b-col cols="7">
          <b-pagination size="md" :total-rows="purchaseAgentCommissionFeesCount" :per-page="searchObject.limit"
                        v-model="currentPaginateIndex"
                        @input="changePaginateState"
          ></b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import {
    mapGetters,
    mapState
  } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import RangeDatePicker from 'vue2-datepicker';
  import moment from 'moment';
  import SaveColumnsBtn from '@/components/common/SaveColumnsBtn';
  import HistoryModalContent from '@/components/modals/HistoryModal';
  import SalesAgentsInvoiceCommissionFeeModal from '@/components/modals/SalesAgentsInvoiceCommissionFeeModal';
  import {
    cleanFilters,
    getArrayByProperty
  } from '@/helpers/commonHelpers';
  import { gridMixins } from '@/mixins';
  import { DATE_FORMAT } from '@/constants';

  export default {
    mixins: [gridMixins],
    components: {
      Multiselect,
      RangeDatePicker,
      SaveColumnsBtn,
      SalesAgentsInvoiceCommissionFeeModal,
      HistoryModalContent,
      'ag-grid-vue': AgGridVue
    },
    data() {
      return {
        searchData: {
          buyers: [],
          commission_type: [],
          currencies: [],
          agents: [],
          suppliers: [],
          purchase_invoice_number: '',
          range_date: '',
          quick_search:''
        },
        searchObject: {
          limit: 100,
          offset: 0
        },
        paginationState: true,
        currentPaginateIndex: 1,
      }
    },
    watch: {
      'searchData.quick_search': {
        deep: true,
        handler () {
          this.setQuickFilter()
        }
      }
    },
    computed: {
      ...mapState('generic', ['currencies', 'commissionTypes', 'currentRoutePermissions']),
      ...mapState('partners', ['agents', 'suppliers', 'customers']),
      ...mapState('accounting', ['gridName', 'purchaseCommissionFeesPinnedRowData', 'purchaseCommissionFeesColumns',
        'purchaseAgentCommissionFeesCount']),
      ...mapGetters('accounting', {
        commissionFees: 'purchaseAgentCommissionFeesList',
        commissionFeesGridOptions: 'commissionFeesGridOptions'
      }),
      countOfViewCommissionFees() {
        return this.commissionFees.length;
      }
    },
    methods: {
      clearData() {
        this.$store.commit('accounting/setPurchaseAgentCommissionFeesList', []);
      },
      cleanFilters() {
        cleanFilters(this.searchData);
      },
      search() {
        this.currentPaginateIndex = 1;
        this.searchObject = {
          ...this.searchObject,
          agents: getArrayByProperty(this.searchData.agents, 'id').toString(),
          suppliers: getArrayByProperty(this.searchData.suppliers, 'id').toString(),
          buyers: getArrayByProperty(this.searchData.buyers, 'id').toString(),
          currencies: getArrayByProperty(this.searchData.currencies, 'id').toString(),
          commission_types: getArrayByProperty(this.searchData.commission_type, 'key').toString(),
          purchase_invoice_nr: this.searchData.purchase_invoice_number,
          purchase_invoice_date_between_after: this.searchData.range_date && this.searchData.range_date[0] ? moment(this.searchData.range_date[0]).format(DATE_FORMAT) : '',
          purchase_invoice_date_between_before: this.searchData.range_date && this.searchData.range_date[1] ? moment(this.searchData.range_date[1]).format(DATE_FORMAT) : ''
        };
        this.$store.dispatch('accounting/loadPurchaseAgentCommissionsFeesList', this.searchObject);
      },
      setQuickFilter() {
        this.commissionFeesGridOptions.api.setQuickFilter(this.searchData.quick_search);
      },
      togglePagination() {
        if (!this.paginationState) {
          delete this.searchObject.limit;
          delete this.searchObject.offset;
        } else {
          this.searchObject.limit = 100;
          this.searchObject.offset = 0;
        }
        this.search();
      },
      changePaginateState() {
        const searchObject = {...this.searchObject, offset: (this.currentPaginateIndex - 1) * this.searchObject.limit};
        this.$store.dispatch('accounting/loadPurchaseAgentCommissionsFeesList', searchObject);
      }
    },
    beforeMount() {
      this.$store.cache.dispatch('generic/loadCommissionTypes', 'purchase_agent');
      this.$store.dispatch('accounting/loadPurchaseAgentCommissionsFeesList', this.searchObject);
      this.$store.dispatch('generic/getCurrencies');
      this.$store.dispatch('partners/getPurchaseAgents');
      this.$store.dispatch('partners/getSuppliers');
      this.$store.dispatch('partners/getCustomers');
    },
    beforeRouteEnter(to, from, next) {
      next(vm => {
        vm.clearData();
      });
    },

  }
</script>

<style scoped>
  .grid {
    height: calc(100vh - 400px);
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/modules/agents/purchase-commission-fees/PurchaseCommissionFeesList.vue